import { createTheme, Theme } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';

export const DRAWER_WIDTH = 180;

// Extend theme to include custom colors
declare module '@mui/material/styles' {
  interface Palette {
    border?: Palette['primary'];
  }
  interface PaletteOptions {
    border?: PaletteOptions['primary'];
  }
  interface TypeText {
    tertiary?: string;
    button?: string;
    inputPlaceholder?: string;
  }
  interface TypeBackground {
    tertiary?: string;
    tooltipBackground?: string;
  }
}

const getDesignTokens = (mode: PaletteMode) => {
  const isLight = mode === 'light';
  return {
    primary: {
      main: '#18cdcd',
      dark: '#018184',
    },
    secondary: {
      main: '#885EC0',
      dark: '#6B3FA3',
    },
    error: {
      main: isLight ? '#f44336' : '#ef5350',
    },
    warning: {
      main: '#F94D4D',
    },
    info: {
      main: '#2196f3',
    },
    success: {
      main: isLight ? '#5AA311' : '#66BB6A',
    },
    background: {
      default: isLight ? '#F9FAFB' : '#0F1318',
      paper: isLight ? '#ffffff' : '#171C23',
      tertiary: isLight ? '#F2F4F7' : '#1E242C',
      tooltipBackground: isLight ? '#344054' : '#E8EAED',
    },
    text: {
      primary: isLight ? '#0C111D' : '#E8EAED',
      secondary: isLight ? '#666666' : '#9AA0A6',
      tertiary: isLight ? '#667085' : '#7A8290',
      disabled: '#aaa',
      button: isLight ? '#042B2F' : '#E8EAED',
      inputPlaceholder: isLight ? '#98A2B3' : '#7A8290',
    },
    divider: isLight ? '#e2e2e2' : '#2A3140',
    border: {
      main: isLight ? '#E4E7EC' : '#2A3140',
    },
    mode,
  };
};

export const createAppTheme = (mode: PaletteMode): Theme =>
  createTheme({
    typography: {
      fontFamily: ['Poppins', 'sans-serif'].join(','),
    },
    palette: getDesignTokens(mode),
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: mode === 'light' ? '#F9FAFB' : '#0F1318',
            color: mode === 'light' ? '#0C111D' : '#E8EAED',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: mode === 'dark' ? {
            boxShadow: 'none',
            border: '1px solid #2A3140',
          } : {},
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: mode === 'dark' ? {
            boxShadow: 'none',
            border: '1px solid #2A3140',
          } : {},
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: mode === 'dark' ? {
            backgroundImage: 'none',
          } : {},
        },
      },
    },
  });

// Backward-compatible exports for incremental migration
export const colors = getDesignTokens('light');
export const theme = createAppTheme('light');
